import React, { useEffect, useRef, useState } from 'react';
import { Tag, Rate, Button } from 'antd';
import ProfileImage from '../UI/ProfileImage/ProfileImage';
import Datestamp from '../Datestamp';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { infoModal } from '../Modals';

const Superlative = ({ children }) => <Tag color="blue">{children}</Tag>;

/** Checks that links contain a protocol, and if not, adds `https://` */
const fixURLProtocol = (link) => {
  if (!link) return link;
  if (link.startsWith('http://') || link.startsWith('https://')) return link;
  return `https://${link}`;
};

const ClinicianCardContainer = styled.div`
  padding: 1em;
  display: flex;
  gap: 1em;
  height: 100%;

  .clinician-bio {
    width: 100%;
    margin: 0.5rem 0 0;
    opacity: 0.8;
    flex-grow: 1;
  }

  .clinician-card-headshot {
    margin-top: 0.3em;
  }

  .clinician-card-id {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  .clinician-card-id-header {
    display: flex;
    gap: 0.75em;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .clinician-card-id-grow {
    flex-grow: 1;
  }

  .clinician-card-next-appointment {
    font-size: 0.85em;
    width: 100%;
    display: flex;
    gap: 0.5em;
    font-weight: 500;
    opacity: 0.8;
  }

  .clinician-card-id-name {
    font-weight: 500;
    font-size: 1.2em;
    opacity: 0.8;
    line-height: 1.5;
    margin-bottom: 0;
  }

  .clinician-card-id-speciality {
    font-size: 0.8em;
    opacity: 0.4;
    line-height: 1;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .clinician-extras {
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    width: 100%;
    margin-top: auto;
  }

  .links {
    display: flex;
    gap: 1em;
    font-size: 0.8em;
    font-weight: 500;
  }

  .tags > :global(.ant-tag) {
    margin-bottom: 0.5em;

    @media (max-width: 768px) {
      font-size: 0.8em;
      margin: 0 0.4em 0.4em 0;
      padding: 0 3px;
    }
  }
`;

const SectionTitle = styled.div`
  width: 100%;
  margin: 0;
  font-size: 0.8em;
  font-weight: 500;
  opacity: 0.5;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const ExtraSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0.5rem 0;
`;

const StyledReadMoreButton = styled(Button)`
  padding: 0;
  font-size: 12px;
  color: #000;
  &:hover {
    color: #000 !important;
    background: transparent !important;
    opacity: 0.8;
  }
`;

const BioParagraph = styled.p`
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ClinicianBio = ({ bioText, clinicianName }) => {
  const { t } = useTranslation('common');
  const bioParagraph = useRef(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const el = bioParagraph.current;
    if (!el) return;

    const getIsBioTextClamped = () => setIsClamped(el.scrollHeight > el.clientHeight);
    getIsBioTextClamped();

    const observer = new ResizeObserver(getIsBioTextClamped);
    observer.observe(el);
    return () => observer.disconnect();
  }, [bioText]);

  if (!bioText) return null;

  const handleShowMore = () =>
    infoModal({
      centered: true,
      title: clinicianName,
      content: bioText,
      okButtonProps: { 'data-testid': 'close-bio-modal-button', text: 'Cancel' }
    });

  return (
    <div className="clinician-bio">
      <BioParagraph ref={bioParagraph}>{bioText}</BioParagraph>
      {isClamped && (
        <StyledReadMoreButton type="text" size="small" data-testid="show-full-bio" onClick={handleShowMore}>
          {t('Read more')}
        </StyledReadMoreButton>
      )}
    </div>
  );
};

const ClinicianCard = ({ clinician, tags, isFirstFastest, timezone }) => {
  const { t } = useTranslation('common');
  const link1 = JSON.parse(clinician.link1);
  const link2 = JSON.parse(clinician.link2);

  const shouldShowInterestsSection = !!clinician.interests?.length;

  const clinicianName = `${clinician?.title || ''} ${clinician?.first_name || ''} ${clinician?.last_name || ''}`;

  return (
    <ClinicianCardContainer>
      <div className="clinician-card-headshot">
        <ProfileImage
          user={clinician?.clin_user_guid}
          style={{ width: 32, height: 32 }}
          alt={t('Clinician profile image')}
        />
      </div>

      <div className="clinician-card-id">
        <div className="clinician-card-id-header">
          <div className="clinician-card-id-grow">
            <h2 className="clinician-card-id-name">{clinicianName}</h2>

            <div className="clinician-card-id-speciality">{clinician?.subtype_desc || ''}</div>
          </div>

          <Rate
            disabled
            allowHalf
            tooltips={Array.from({ length: 5 }, (_, i) =>
              clinician?.prems_rank === -1 ? 'No score' : `${Math.round(clinician?.prems_rank * 5 * 2) / 2} out of 5`
            )}
            defaultValue={clinician?.prems_rank === -1 ? 0 : Math.round(clinician?.prems_rank * 5 * 2) / 2}
          />

          <div className="clinician-card-id-superlatives">
            {isFirstFastest && <Superlative>{t('First available')}</Superlative>}
            {clinician?.last_seen_flag && <Superlative>{t('Last Seen')}</Superlative>}
            {clinician?.best_match_flag && <Superlative>{t('Best Match')}</Superlative>}
          </div>
        </div>

        <ClinicianBio bioText={clinician?.bio} clinicianName={clinicianName} />

        {shouldShowInterestsSection && (
          <ExtraSection>
            <SectionTitle>{t('Clinical Interests')}</SectionTitle>
            {clinician.interests.map((interest) => (
              <Tag key={interest.interest_code}>{interest.interest_desc}</Tag>
            ))}
          </ExtraSection>
        )}

        <ExtraSection>
          <SectionTitle>{t('Language')}</SectionTitle>
          <Tag>{clinician?.lang_desc}</Tag>
          {clinician?.add_langs
            ?.map((lang) => lang?.lang_desc)
            ?.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
        </ExtraSection>

        <div className="clinician-extras">
          <div className="clinician-card-next-appointment">
            {t('Next available appointment:')}
            <span>
              {clinician?.first_slot ? (
                <Datestamp
                  date={clinician?.first_slot || null}
                  timezone={timezone}
                  format="Date @ Time (long, 24h) - Friday, March 3rd, 2022 @ 08:57"
                />
              ) : (
                'Unknown'
              )}
            </span>
          </div>

          <div className="tags">
            {tags?.slice(0, 5)?.map((tag) => (
              <Tag key={tag} color="var(--productPrimary)">
                {tag}
              </Tag>
            ))}
          </div>
          <div className="links">
            {link1?.link && (
              <a href={fixURLProtocol(link1?.link)} rel="noreferrer" target="_blank">
                {link1?.text}
              </a>
            )}
            {link2?.link && (
              <a href={fixURLProtocol(link2?.link)} rel="noreferrer" target="_blank">
                {link2?.text}
              </a>
            )}
          </div>
        </div>
      </div>
    </ClinicianCardContainer>
  );
};

export default ClinicianCard;
